import { ReceiptSupplier, supplierName } from '@/types/receipt'
import { QuestionnaireState } from '@/types/store'
import useVuex from '@/utils/useVuex'
import {
  computed,
  onBeforeMount,
  reactive,
  SetupContext,
} from '@vue/composition-api'
import { QuestionnaireEntity } from '../../../api/@types'
import moment from 'moment'

type Additional = {
  isRequiredCheck: string
  totalLimitDisplay: string | number
  totalLimit: string | number
  resetLimit: string | number
  pobCategory: string | number
  dailyUpperLimitByJan: string | number
  chainStore: string | number
  dailyUpperLimitByChain: string | number
  dailyUpperLimit: string | number
  answerUpperLimit: string | number
  openPeriod: string
  targetSupplier: string
  targetGender: string
  targetAge: string
  targetMarriage: string
  targetPrefecture: string
}

export type QuestionnaireDetail = Additional &
  Omit<QuestionnaireEntity, keyof Additional>

export const useQuestionnaireDetail = (context: SetupContext) => {
  const store = context.root.$store
  const fromDate = (date: string): string => {
    return moment(date).format('YYYY/MM/DD HH:mm')
  }

  const state = reactive({
    questionnaire: useVuex<QuestionnaireState['questionnaire']>(
      context,
      () => store.getters['questionnaire/questionnaire'],
    ),
  })

  const genderEnumToString = (
    type: QuestionnaireEntity['targetGender'],
  ): string => {
    switch (type) {
      case 'male':
        return '男性'
      case 'female':
        return '女性'
      case 'other':
        return '不明'
      case 'male_female':
        return '男性、女性'
      case 'male_other':
        return '男性、不明'
      case 'female_other':
        return '女性、不明'
      case 'all':
        return '全て'
      default:
        return '(未設定)'
    }
  }

  const marriageEnumToList = (
    type: QuestionnaireEntity['targetMarriage'],
  ): string => {
    switch (type) {
      case 'married':
        return '既婚'
      case 'unmarried':
        return '未婚'
      case 'other':
        return '不明'
      case 'married_unmarried':
        return '既婚、未婚'
      case 'married_other':
        return '既婚、不明'
      case 'unmarried_other':
        return '未婚、不明'
      default:
        return '(未設定)'
    }
  }

  const questionnaire = computed<QuestionnaireDetail>(() => {
    return {
      ...state.questionnaire,
      isRequiredCheck: state.questionnaire.isRequiredCheck ? 'あり' : 'なし',
      totalLimitDisplay: state.questionnaire.totalLimitDisplay || '(未設定)',
      totalLimit: state.questionnaire.totalLimit || '(未設定)',
      resetLimit: state.questionnaire.resetLimit || '(未設定)',
      pobCategory:
        state.questionnaire.pobCategories
          .map(cate => cate.jicfsCategorySS)
          .join(', ') || '(未設定)',
      dailyUpperLimitByJan:
        state.questionnaire.dailyUpperLimitByJan || '(未設定)',
      chainStore:
        state.questionnaire.chianStores.map(store => store.name).join(', ') ||
        '(未設定)',
      dailyUpperLimitByChain:
        state.questionnaire.dailyUpperLimitByChain || '(未設定)',
      dailyUpperLimit: state.questionnaire.dailyUpperLimit || '(未設定)',
      answerUpperLimit: state.questionnaire.answerUpperLimit || '(未設定)',
      openPeriod: `${state.questionnaire.releaseAt &&
        fromDate(state.questionnaire.releaseAt)} ~ ${state.questionnaire
        .finishAt && fromDate(state.questionnaire.finishAt)}`,
      targetSupplier: supplierName(
        state.questionnaire.targetSupplier as ReceiptSupplier,
      ),
      targetGender: genderEnumToString(state.questionnaire.targetGender),
      targetAge:
        !state.questionnaire.targetLowerLimitAge &&
        !state.questionnaire.targetUpperLimitAge
          ? '全年齢対象'
          : `${state.questionnaire.targetLowerLimitAge} ~ ${state.questionnaire.targetUpperLimitAge}`,
      targetMarriage: marriageEnumToList(state.questionnaire.targetMarriage),
      targetPrefecture: state.questionnaire.targetPrefecture.join(', '),
      fixedQuestionnaireUrl: state.questionnaire.fixedQuestionnaireUrl,
      imageUrl: state.questionnaire.imageUrl,
      parentId: null,
      parent: null,
    }
  })

  const pages = computed(() => state.questionnaire.pages)

  onBeforeMount(() =>
    store.dispatch(
      'questionnaire/getQuestionnaire',
      context.root.$route.params.id,
    ),
  )

  return { questionnaire, pages }
}
